// Paleta única de etiquetas (C15 1.14.3 · Etiquetas R1): la definición que usan todas las pantallas (Etiquetas,
// Contactos, el panel del contacto en Conversaciones y la guarda check_tag_palette). Cada color tiene su relleno y el
// texto de su par, iguales en claro y en oscuro. El orden de la lista es el orden automático de las etiquetas nuevas.

export type TagColorOption = {
  /** Clave estable del color (nombre en es-PE sin tildes). */
  key: string;
  /** Clave del texto con su nombre (aria-label y title del selector). */
  label: string;
  /** Relleno de la etiqueta; es lo que se guarda al elegirlo. */
  fill: string;
  /** Texto sobre el relleno (≥ 5:1). */
  text: string;
};

const option = (key: string, fill: string, text: string): TagColorOption => ({
  key,
  label: `COLOR_${key.toUpperCase()}`,
  fill,
  text,
});

export const palette: TagColorOption[] = [
  option("lima", "#97F05D", "#1E1A17"),
  option("ciruela", "#961A91", "#FFFFFF"),
  option("celeste", "#22B1F6", "#1E1A17"),
  option("naranja", "#F2591B", "#1E1A17"),
  option("oliva", "#4E5D0B", "#FFFFFF"),
  option("rosa", "#FDACE9", "#1E1A17"),
  option("azul", "#0C5893", "#FFFFFF"),
  option("amarillo", "#F1B726", "#1E1A17"),
  option("turquesa", "#1FA991", "#1E1A17"),
  option("lavanda", "#8A7DFA", "#1E1A17"),
  option("frambuesa", "#F14E78", "#1E1A17"),
  option("pistacho", "#8D9D1A", "#1E1A17"),
  option("cian", "#31EFFA", "#1E1A17"),
  option("salmon", "#FCA89A", "#1E1A17"),
  option("orquidea", "#D25BCB", "#1E1A17"),
  option("bosque", "#13794E", "#FFFFFF"),
  option("menta", "#31F7BF", "#1E1A17"),
  option("granate", "#AA124E", "#FFFFFF"),
  option("verde", "#1FAF46", "#1E1A17"),
  option("caramelo", "#C7801A", "#1E1A17"),
];

/** Orden del selector de color (C45): por matiz, de granate a rosa. */
export const selectorOrder = [
  "granate",
  "frambuesa",
  "salmon",
  "naranja",
  "caramelo",
  "amarillo",
  "pistacho",
  "oliva",
  "lima",
  "verde",
  "bosque",
  "menta",
  "turquesa",
  "cian",
  "celeste",
  "azul",
  "lavanda",
  "ciruela",
  "orquidea",
  "rosa",
].map((name) => palette.find((item) => item.key === name)!);

/**
 * Los 10 colores de antes (C15 1.20), guardados como su texto en claro, en el orden de su selector: salmón, durazno,
 * amarillo, oliva, verde, turquesa, celeste, lila, rosa y beige. Se muestran con su equivalente; el valor guardado no cambia.
 */
const previous: Record<string, string> = {
  "#932b2a": "salmon",
  "#8c4400": "naranja",
  "#6c5005": "amarillo",
  "#505103": "lima",
  "#225a31": "menta",
  "#005c58": "cian",
  "#005574": "celeste",
  "#554077": "lavanda",
  "#713654": "rosa",
  "#66442c": "caramelo",
};

type Lab = [number, number, number];

function lab(hex: string): Lab {
  const value = parseInt(hex.slice(1), 16);
  const linear = (channel: number) => {
    const c = channel / 255;
    return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  const [r, g, b] = [linear(value >> 16), linear((value >> 8) & 255), linear(value & 255)];
  const x = (0.4124564 * r + 0.3575761 * g + 0.1804375 * b) / 0.95047;
  const y = 0.2126729 * r + 0.7151522 * g + 0.072175 * b;
  const z = (0.0193339 * r + 0.119192 * g + 0.9503041 * b) / 1.08883;
  const f = (t: number) => (t > 216 / 24389 ? Math.cbrt(t) : (24389 / 27 * t + 16) / 116);
  return [116 * f(y) - 16, 500 * (f(x) - f(y)), 200 * (f(y) - f(z))];
}

/** Diferencia de color CIEDE2000 entre dos #RRGGBB. */
export function deltaE00(first: string, second: string): number {
  const [l1, a1, b1] = lab(first);
  const [l2, a2, b2] = lab(second);
  const rad = Math.PI / 180;
  const c1 = Math.hypot(a1, b1),
    c2 = Math.hypot(a2, b2);
  const cMean = (c1 + c2) / 2;
  const g = 0.5 * (1 - Math.sqrt(cMean ** 7 / (cMean ** 7 + 25 ** 7)));
  const ap1 = a1 * (1 + g),
    ap2 = a2 * (1 + g);
  const cp1 = Math.hypot(ap1, b1),
    cp2 = Math.hypot(ap2, b2);
  const hue = (bb: number, aa: number) => (aa === 0 && bb === 0 ? 0 : ((Math.atan2(bb, aa) / rad) + 360) % 360);
  const hp1 = hue(b1, ap1),
    hp2 = hue(b2, ap2);
  const dL = l2 - l1,
    dC = cp2 - cp1;
  let dh = 0;
  if (cp1 * cp2 !== 0) dh = Math.abs(hp2 - hp1) <= 180 ? hp2 - hp1 : hp2 - hp1 > 180 ? hp2 - hp1 - 360 : hp2 - hp1 + 360;
  const dH = 2 * Math.sqrt(cp1 * cp2) * Math.sin((dh * rad) / 2);
  const lMean = (l1 + l2) / 2,
    cpMean = (cp1 + cp2) / 2;
  let hMean = hp1 + hp2;
  if (cp1 * cp2 !== 0)
    hMean = Math.abs(hp1 - hp2) <= 180 ? hMean / 2 : hMean < 360 ? (hMean + 360) / 2 : (hMean - 360) / 2;
  const t =
    1 -
    0.17 * Math.cos((hMean - 30) * rad) +
    0.24 * Math.cos(2 * hMean * rad) +
    0.32 * Math.cos((3 * hMean + 6) * rad) -
    0.2 * Math.cos((4 * hMean - 63) * rad);
  const sl = 1 + (0.015 * (lMean - 50) ** 2) / Math.sqrt(20 + (lMean - 50) ** 2);
  const sc = 1 + 0.045 * cpMean;
  const sh = 1 + 0.015 * cpMean * t;
  const rt =
    -2 *
    Math.sqrt(cpMean ** 7 / (cpMean ** 7 + 25 ** 7)) *
    Math.sin(60 * Math.exp(-(((hMean - 275) / 25) ** 2)) * rad);
  return Math.sqrt((dL / sl) ** 2 + (dC / sc) ** 2 + (dH / sh) ** 2 + rt * (dC / sc) * (dH / sh));
}

const hex = (color: string | null | undefined) => {
  const value = color?.trim().toLowerCase() ?? "";
  return /^#[0-9a-f]{6}$/.test(value) ? value : null;
};

/**
 * El color de la paleta con que se muestra un color guardado: el mismo si es de los 20, su equivalente si es de los 10
 * de antes y, si es otro #RRGGBB, el de menor ΔE00. Sin color (null o vacío) o un valor inválido: null (neutra).
 */
export function inPalette(color: string | null | undefined): TagColorOption | null {
  const value = hex(color);
  if (!value) return null;
  const exact = palette.find((item) => item.fill.toLowerCase() === value);
  if (exact) return exact;
  const mapped = previous[value];
  if (mapped) return palette.find((item) => item.key === mapped)!;
  return palette.reduce((best, item) => (deltaE00(value, item.fill) < deltaE00(value, best.fill) ? item : best));
}

export const key = (color: string | null | undefined) => inPalette(color)?.key ?? null;

export const colored = (color: string | null | undefined) => inPalette(color) !== null;

/**
 * El color que viene elegido en una etiqueta nueva (C15 1.14.3): el que sigue, en el orden automático, al de la última
 * etiqueta creada en la empresa (con su color ya mapeado). Sin etiquetas, o si la última no tiene color, Lima; después
 * de Caramelo vuelve a Lima.
 */
export function forNewTag(lastColor: string | null | undefined): string {
  const last = inPalette(lastColor);
  const index = last ? palette.indexOf(last) : -1;
  return palette[(index + 1) % palette.length]!.fill;
}

/** Las dos variables que pintan una etiqueta (relleno y texto); null = neutra. */
export function tagStyle(color: string | null | undefined): string | null {
  const shown = inPalette(color);
  return shown ? `--contact-tag-background:${shown.fill};--contact-tag-foreground:${shown.text}` : null;
}

/** Convierte "a:b;c:d" en el objeto style de React con las variables CSS. */
export function styleObject(css: string | null): React.CSSProperties | undefined {
  if (!css) return undefined;
  return Object.fromEntries(
    css
      .split(";")
      .filter(Boolean)
      .map((part) => {
        const index = part.indexOf(":");
        return [part.slice(0, index).trim(), part.slice(index + 1).trim()];
      }),
  ) as React.CSSProperties;
}

// J4 · C22 1.14.4: los colores que el cliente ya tiene (la última página de Etiquetas) sirven para pintar la píldora del
// filtro de Contactos desde el primer cuadro, antes de que llegue el catálogo. Solo en memoria: al recargar no hay.
const knownColors = new Map<string, string | null>();

/** Guarda el color de cada etiqueta vista (nombre en minúsculas, como el catálogo de Contactos). */
export function rememberTagColors(tags: { name: string; color?: string | null }[]): void {
  for (const tag of tags) knownColors.set(tag.name.toLowerCase(), tag.color ?? null);
}

/** El color recordado de una etiqueta; undefined si el cliente todavía no lo conoce. */
export const knownTagColor = (name: string): string | null | undefined => knownColors.get(name.toLowerCase());
